import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ActionItem } from "./ActionCard";
import { format, isSameDay, parseISO, startOfDay } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Clock, FileText, ChevronRight, Calendar as CalendarIcon } from "lucide-react";

interface ActionsCalendarProps {
  items: ActionItem[];
  onItemClick: (item: ActionItem) => void;
}

export function ActionsCalendar({ items, onItemClick }: ActionsCalendarProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);

  // Map items to their dates
  const itemsByDate = useMemo(() => {
    const map = new Map<string, ActionItem[]>();
    
    items.forEach(item => {
      const dateStr = item.expiresAt || item.dueDate;
      if (dateStr) {
        const date = format(parseISO(dateStr), 'yyyy-MM-dd');
        const existing = map.get(date) || [];
        map.set(date, [...existing, item]);
      }
    });
    
    return map;
  }, [items]);

  // Get items for selected date
  const selectedItems = useMemo(() => {
    if (!selectedDate) return [];
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    return itemsByDate.get(dateStr) || [];
  }, [selectedDate, itemsByDate]);

  // Custom day content to show status dots
  const modifiers = useMemo(() => {
    const expired: Date[] = [];
    const urgent: Date[] = [];
    const upcoming: Date[] = [];

    items.forEach(item => {
      const dateStr = item.expiresAt || item.dueDate;
      if (!dateStr) return;
      
      const date = startOfDay(parseISO(dateStr));
      const days = item.daysUntilExpiry ?? Math.ceil((date.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      
      if (days < 0) {
        expired.push(date);
      } else if (days <= 7) {
        urgent.push(date);
      } else {
        upcoming.push(date);
      }
    });

    return { expired, urgent, upcoming };
  }, [items]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Calendar */}
      <Card>
        <CardContent className="p-4">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={setSelectedDate}
            locale={fr}
            className="mx-auto"
            modifiers={{
              expired: modifiers.expired,
              urgent: modifiers.urgent,
              upcoming: modifiers.upcoming,
            }}
            modifiersClassNames={{
              expired: "relative after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1.5 after:h-1.5 after:rounded-full after:bg-destructive",
              urgent: "relative after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1.5 after:h-1.5 after:rounded-full after:bg-warning",
              upcoming: "relative after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1.5 after:h-1.5 after:rounded-full after:bg-primary",
            }}
          />
          
          {/* Legend */}
          <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <div className="w-2 h-2 rounded-full bg-destructive" />
              Expiré
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <div className="w-2 h-2 rounded-full bg-warning" />
              Urgent
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <div className="w-2 h-2 rounded-full bg-primary" />
              À venir
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Selected Day Details */}
      <Card>
        <CardContent className="p-4">
          <AnimatePresence mode="wait">
            {selectedDate ? (
              <motion.div
                key={format(selectedDate, 'yyyy-MM-dd')}
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="space-y-4"
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">
                    {format(selectedDate, "EEEE d MMMM", { locale: fr })}
                  </h3>
                  <Badge variant="secondary">
                    {selectedItems.length} action{selectedItems.length !== 1 ? 's' : ''}
                  </Badge>
                </div>

                {selectedItems.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <Clock className="w-8 h-8 text-muted-foreground/50 mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Aucune action prévue ce jour
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {selectedItems.map(item => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-lg border cursor-pointer",
                          "hover:bg-accent/50 transition-colors active:scale-[0.98]",
                          item.daysUntilExpiry !== undefined && item.daysUntilExpiry < 0 && "border-destructive/30 bg-destructive/5"
                        )}
                        onClick={() => onItemClick(item)}
                      >
                        <div className={cn(
                          "w-2 h-2 rounded-full shrink-0",
                          item.daysUntilExpiry !== undefined && item.daysUntilExpiry < 0 ? "bg-destructive" :
                          item.daysUntilExpiry !== undefined && item.daysUntilExpiry <= 7 ? "bg-warning" :
                          "bg-primary"
                        )} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{item.title}</p>
                          <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            {item.documentTitle || "Document"}
                          </p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col items-center justify-center py-8 text-center"
              >
                <CalendarIcon className="w-8 h-8 text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground">
                  Sélectionnez une date pour voir les actions
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>
    </div>
  );
}
